import { a, config, SpringValue, useSpringValue } from '@react-spring/three';
import type { ThreeElements } from '@react-three/fiber';
import * as THREE from 'three';
import { useDragControls, type UseDragControlsOptions } from './useDragControls';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { toVec3 } from '@slope/three';

const vec3 = /*@__PURE__ */ new THREE.Vector3();

export type DragControlsProps = ThreeElements['group'] &
  Pick<
    UseDragControlsOptions,
    'autoTransform' | 'project' | 'anchor' | 'onDragStart' | 'onDragEnd' | 'onHover'
  > & {
    onDrag?: (matrix: THREE.Matrix4) => void;
    onChange?: (position: THREE.Vector3Tuple) => void;
    spring?: boolean;
    springRef?: React.RefObject<SpringValue<THREE.Vector3Tuple>>;
  };

export const DragControls = forwardRef<THREE.Group, DragControlsProps>(
  (
    {
      children,
      anchor = 'bottom',
      position: positionProp,
      autoTransform = false,
      spring = true,
      springRef,
      project,
      onChange,
      onDrag,
      onDragStart,
      onDragEnd,
      onHover,
      ...restProps
    },
    forwardedRef,
  ) => {
    const matrix = useMemo(() => new THREE.Matrix4(), []);

    // @ts-expect-error asd
    const position = useSpringValue(toVec3(positionProp ?? 0, vec3).toArray(), {
      config: config.stiff,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange: (value: any) => {
        matrix.setPosition(vec3.fromArray(value));
        onChange?.(value);
      },
    });

    const attributes = useDragControls({
      matrix,
      project,
      anchor,
      autoTransform,
      onDragStart,
      onDrag: (localMatrix) => {
        if (spring) {
          position.start(vec3.setFromMatrixPosition(localMatrix).toArray());
        }
        onDrag?.(localMatrix);
      },
      onDragEnd,
      onHover,
    });

    useImperativeHandle(springRef, () => position);

    useImperativeHandle(forwardedRef, () => attributes.ref.current!);

    return (
      <a.group {...attributes} {...restProps}>
        {children}
      </a.group>
    );
  },
);
